// src/components/Study/Study.js

import React, { useState, useEffect, useCallback } from 'react';
import { fetchQuizData } from '../../services/dataService';
import Question from '../Shared/Question';
import Feedback from '../Shared/Feedback';
import StudyResults from './StudyResults';
import styles from '../Exam/Exam.module.css';
import { shuffleAnswers } from '../../services/quizArrayUtilities';
import { updateSelectedAnswers, handleCheckAnswer } from '../../services/quizAnswerHandlers';
import { useSettings } from '../Settings/SettingsContext';

const plural = (count, forms) => {
    if (count === 1) return forms[0];
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return forms[1];
    return forms[2];
};

const Study = ({ listId }) => {
    const { forceMultiChoice, requiredRepetitions } = useSettings();

    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
    const [shuffledAnswers, setShuffledAnswers] = useState([]);
    const [shuffleEnabled, setShuffleEnabled] = useState(false);
    const [startTime] = useState(Date.now());
    const [totalTime, setTotalTime] = useState(0);
    const [progressCounts, setProgressCounts] = useState({});

    const shuffleCurrentAnswers = useCallback((answers) => {
        const shuffled = shuffleAnswers(answers, shuffleEnabled);
        setShuffledAnswers(shuffled);
    }, [shuffleEnabled]);

    useEffect(() => {
        fetchQuizData(listId)
            .then(data => {
                setQuestions(data);
                setProgressCounts({});
                setCurrentQuestionIndex(0);
                setSelectedAnswers([]);
                if (data.length > 0) shuffleCurrentAnswers(data[0].answers);
            })
            .catch(error => console.error(error));
    }, [listId, shuffleCurrentAnswers]);

    useEffect(() => {
        if (questions.length > 0 && currentQuestionIndex < questions.length) {
            shuffleCurrentAnswers(questions[currentQuestionIndex].answers);
        }
    }, [shuffleEnabled, questions, currentQuestionIndex, shuffleCurrentAnswers]);

    const handleAnswerSelection = (index) => {
        const currentQuestion = questions[currentQuestionIndex];
        const isSingleChoice = !forceMultiChoice && currentQuestion && currentQuestion.correct_answers.length === 1;
        setSelectedAnswers(prev =>
            updateSelectedAnswers(prev, index, isSingleChoice)
        );
    };

    const handleCheckAnswerClick = () => {
        const currentQuestion = questions[currentQuestionIndex];
        const feedback = handleCheckAnswer(currentQuestion, selectedAnswers, shuffledAnswers);
        setFeedback(feedback);
        setIsFeedbackVisible(true);
    };

    const handleNextQuestionClick = () => {
        const currentQuestion = questions[currentQuestionIndex];
        const correctAnswers = currentQuestion.correct_answers.map(ans => ans - 1);
        const selectedShuffledIndexes = selectedAnswers.map(index => shuffledAnswers[index].index);
        const isCorrect = selectedShuffledIndexes.sort().toString() === correctAnswers.sort().toString();

        setIsFeedbackVisible(false);
        setFeedback(null);

        setProgressCounts(prev => {
            const prevCount = prev[currentQuestionIndex] || 0;
            const newCount = isCorrect ? prevCount + 1 : prevCount;
            return { ...prev, [currentQuestionIndex]: newCount };
        });

        setTimeout(() => {
            let nextIdx = currentQuestionIndex;
            for (let i = 1; i <= questions.length; i++) {
                const candidate = (currentQuestionIndex + i) % questions.length;
                if ((progressCounts[candidate] || 0) < requiredRepetitions) {
                    nextIdx = candidate;
                    break;
                }
            }
            setCurrentQuestionIndex(nextIdx);
            setSelectedAnswers([]);
        }, 100);
    };

    const handleRestart = () => {
        window.location.reload();
    };

    const handleReturnToHome = () => {
        window.location.href = '/';
    };

    const countsByRepetition = Array(requiredRepetitions + 1).fill(0);
    questions.forEach((_, idx) => {
        const c = progressCounts[idx] || 0;
        if (c >= requiredRepetitions) {
            countsByRepetition[requiredRepetitions]++;
        } else {
            countsByRepetition[c]++;
        }
    });
    const learned = countsByRepetition[requiredRepetitions];
    const toLearn = countsByRepetition[0];

    useEffect(() => {
        if (questions.length > 0 && learned === questions.length) {
            setTotalTime(Date.now() - startTime);
        }
    }, [learned, questions.length, startTime]);

    if (questions.length > 0 && learned === questions.length) {
        return (
            <StudyResults
                learnedQuestionsCount={learned}
                totalQuestions={questions.length}
                totalTime={totalTime}
                handleRestart={handleRestart}
                handleReturnToHome={handleReturnToHome}
                listId={listId}
            />
        );
    }

    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) {
        return <div>Ładowanie pytań...</div>;
    }
    const isSingleChoice = !forceMultiChoice && currentQuestion.correct_answers.length === 1;
    const currentProgress = progressCounts[currentQuestionIndex] || 0;

    const getStageLabel = (rep, count) => {
        let timesWord = (rep + 1) === 1 ? 'raz' : 'razy';
        return (
            <p className={styles.textElement} key={rep + 1}>
                W trakcie nauki: {count} {plural(count, ['pytanie', 'pytania', 'pytań'])}
                &nbsp;({rep + 1}/{requiredRepetitions} {timesWord} poprawnie)
            </p>
        );
    };

    return (
        <div className={styles.quizContainer}>
            <Question
                question={currentQuestion.question}
                answers={shuffledAnswers.map(sa => sa.answer)}
                selectedAnswers={selectedAnswers}
                handleAnswerSelection={handleAnswerSelection}
                image={currentQuestion.image}
                isSingleChoice={isSingleChoice}
            />
            <button className={styles.quizButton} onClick={handleCheckAnswerClick}>Sprawdź odpowiedź</button>
            <button className={styles.quizButton} onClick={handleNextQuestionClick}>Next</button>
            {isFeedbackVisible && <Feedback feedback={feedback} question={currentQuestion} />}
            <div style={{ fontSize: 14, margin: '16px 0 0 0', color: '#555' }}>
                To pytanie powtórzono poprawnie: <b>{currentProgress}/{requiredRepetitions}</b> {requiredRepetitions === 1 ? 'raz' : 'razy'}
            </div>
            <div style={{ marginTop: 24 }}>
                <b>Postęp nauki:</b>
                <p className={styles.textElement}>
                    Do nauki: {toLearn} {plural(toLearn, ['pytanie', 'pytania', 'pytań'])}
                </p>
                {requiredRepetitions > 1 && (
                    <>
                        {Array.from({ length: requiredRepetitions - 1 }).map((_, i) =>
                            countsByRepetition[i + 1] > 0 && getStageLabel(i, countsByRepetition[i + 1])
                        )}
                    </>
                )}
                <p className={styles.textElement} style={{ color: 'green' }}>
                    Nauczone: {learned} {plural(learned, ['pytanie', 'pytania', 'pytań'])}
                    {requiredRepetitions > 1 && ` (zaliczone ${requiredRepetitions} ${requiredRepetitions === 1 ? 'raz' : 'razy'})`}
                </p>
            </div>
            <div className={styles.shuffleOption}>
                <label>
                    <input
                        type="checkbox"
                        checked={shuffleEnabled}
                        onChange={() => setShuffleEnabled(!shuffleEnabled)}
                    />
                    Mieszaj kolejność odpowiedzi
                </label>
            </div>
        </div>
    );
};

export default Study;
